import {observer} from 'mobx-react-lite'
import React, {useEffect, useMemo} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {ApiBoardSettings} from '@api/components/api-board-settings'
import {UIHandlers} from '@api/model/ui-handlers'
import {addingAllowedToBoard} from '@client/hub-actions-allowed'
import {MovementClient} from '@client/movement-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {TaskTemplateClient} from '@client/task-template-client'
import {Tracking, TrackingEvent} from '@common/tracking'
import {
  ExtensionsGalleryBanner,
  ExtensionsGalleryLink
} from '@components/board/board-settings/extensions-gallery-banner'
import {CustomFieldsEditor} from '@components/board/crm/custom-fields-editor'
import {GeneralBoardSettingsInner} from '@components/board/general-board-settings-popup/general-board-settings-popup'
import {BoardAppearance} from '@components/board-appearance/board-appearance'
import {PopupFull} from '@components/common/base/popup-full'
import {Tabs} from '@components/common/base/tabs'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {crmStore} from '@components/crm/crm-store'
import {MovementTemplates} from '@components/movement-templates'
import {ShareInner} from '@components/sharing/share-popup/share-popup'
import {MyStickers} from '@components/sticker/my-stickers/my-stickers'
import {TaskTemplates} from '@components/task-templates/task-templates'
import {useBoard} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {UserAccess} from '@extensions/user-access'
import {
  boardSettingsState as state,
  boardSettingsStore as store,
  TabId
} from './board-settings-store'

const TwSidebar = tw.div`
  w-200
  max-w-200
  flex
  flex-col
  min-h-0
  min-w-0
  shadow-border-r-1
  shadow-grey-l60
`

const TwContainer = tw.div`
  mx-auto
  flex-1
  flex
  min-h-0
  min-w-0
  w-[930px]
`

const TwMain = tw.div`
  flex-1
  mt-8
  flex
  flex-col
  min-h-0
  min-w-0
`

const BoardSettings = observer((): JSX.Element | null => {
  if (!state.isOpened) {
    return null
  }

  return <BoardSettingsBase />
})

const BoardSettingsBase = observer((): JSX.Element | null => {
  const board = useBoard()
  const {tabs, hasExtensions, shouldPromoteExtensions} = useTabs()
  const currentTab = useMemo(
    () => tabs.find(tab => tab.id === state.currentTabId) || tabs[0],
    [state.currentTabId]
  )

  useEffect(() => {
    const disabledTabs = tabs.filter(tab => tab.disabled).map(tab => tab.id)
    store.setDisabledTabs(disabledTabs)
    store.setAllTabsDisabled(disabledTabs.length === tabs.length)
  }, [tabs])

  const onChangeTab = (id: TabId) => {
    store.setCurrentTabId(id)
  }

  const CurrentInnerComponent = currentTab.Component

  return (
    <PopupFull
      close={store.toggle}
      title={Lang.context.board.settingFor(board.title)}
      withoutContentOverflowScroll={true}
    >
      <TwContainer>
        <TwSidebar>
          <WithPerfectScrollbar className="h-full flex flex-col gap-8">
            <Tabs
              tabs={tabs}
              defaultActiveId={currentTab.id}
              onChange={onChangeTab}
              onClick={store.onHasChangesCb}
              direction="col"
              canChangeTab={!state.hasChanges}
              className="pr-8"
              lineClamp={2}
            />
            {hasExtensions && (
              <ExtensionsGalleryLink
                label={Lang.msg.extensions.boardSettings.allExtensions}
                onClick={() => {
                  Tracking.send(TrackingEvent.boardSettingsClickOnAllExtensions)
                }}
              />
            )}
          </WithPerfectScrollbar>

          {shouldPromoteExtensions && <ExtensionsGalleryBanner />}
        </TwSidebar>
        <TwMain>
          {currentTab.disabled ? (
            <p className="px-24">{Lang.msg.notEnoughPermissions}</p>
          ) : (
            <WithPerfectScrollbar className="px-24">
              {CurrentInnerComponent ? (
                <CurrentInnerComponent
                  {...state.additionalTabInfo}
                  defaultPage={state.page}
                />
              ) : null}
            </WithPerfectScrollbar>
          )}
        </TwMain>
      </TwContainer>
    </PopupFull>
  )
})

export interface BoardSettingsTab {
  id: TabId
  title: string
  Component?: React.ComponentType<any>
  separator?: boolean
  className?: string
  disabled?: boolean
  helpText?: string
}

interface TabsInfo {
  tabs: BoardSettingsTab[]
  hasExtensions: boolean
  shouldPromoteExtensions: boolean
}

const createEmptyTabsInfo = (): TabsInfo => {
  return {
    tabs: [],
    hasExtensions: false,
    shouldPromoteExtensions: false
  }
}

const useTabs = (): TabsInfo => {
  const board = useBoard()

  return useMemo<TabsInfo>(() => {
    if (!board) {
      return createEmptyTabsInfo()
    }

    const box = Sync.get({companyId: board.companyId})
    const project = box.getHub(board.location)
    if (!project) {
      return createEmptyTabsInfo()
    }
    const role = UserAccess.getRoleInProject(globals.currentUserId, project)
    const boardSettings = PermissionsChecker.checkBoardSettings(board)

    const showGear = PermissionsChecker.checkEditBoardStickers(board)
    const canAddToAnyColumn = addingAllowedToBoard(board)
    const isCrmBoard = crmStore.isCrmBoard(board)

    const tabs: BoardSettingsTab[] = []

    if (!isCrmBoard) {
      tabs.push({
        id: 'boardSettings',
        title: Lang.msg.boardSettingsMain,
        Component: GeneralBoardSettingsInner,
        disabled: !boardSettings,
        helpText: Lang.msg.notEnoughPermissions
      })
    }

    tabs.push(
      {
        id: 'boardAppearance',
        title: Lang.msg.boardSettingsAppearance,
        Component: BoardAppearance,
        disabled: !boardSettings,
        helpText: Lang.msg.notEnoughPermissions
      },
      {
        id: 'stickers',
        title: Lang.msg.boardSettingsStickers,
        Component: MyStickers,
        disabled: !showGear,
        helpText: Lang.msg.notEnoughPermissions
      }
    )

    if (!isCrmBoard) {
      tabs.push({
        id: 'boardSharing',
        title: Lang.msg.boardSettingsSharing,
        Component: ShareInner,
        disabled: !PermissionsChecker.isAdmin() && role !== 'admin',
        helpText: Lang.msg.notEnoughPermissions
      })

      if (MovementClient.hasFeature()) {
        tabs.push({
          id: 'movementTemplates',
          title: Lang.msg.boardSettingsMoveTasks,
          Component: MovementTemplates,
          disabled: !boardSettings,
          helpText: Lang.msg.notEnoughPermissions
        })
      }

      if (TaskTemplateClient.hasFeature()) {
        tabs.push({
          id: 'taskTemplates',
          title: Lang.msg.boardSettingsTaskTemplate,
          Component: TaskTemplates,
          disabled: !canAddToAnyColumn,
          helpText: Lang.msg.notEnoughPermissions
        })
      }
    }

    if (isCrmBoard) {
      tabs.push({
        id: 'customFields',
        title: Lang.msg.crm.boardSettings.customFields,
        Component: () => <CustomFieldsEditor project={project} />
      })
    }

    const apiTabs = UIHandlers.boardSettings(board)
    const hasExtensions = apiTabs.length > 0 && !isCrmBoard
    const shouldPromoteExtensions = !hasExtensions && !isCrmBoard

    if (hasExtensions) {
      tabs.push({
        id: 'separate-api',
        title: Lang.msg.extensions.extensionsSettings,
        separator: true,
        className:
          'text-primary text-sm-bold !pt-16 !pl-0 !pb-8 cursor-default',
        helpText: Lang.msg.notEnoughPermissions
      })

      for (const el of apiTabs) {
        tabs.push({
          ...el,
          Component: () => <ApiBoardSettings id={el.id as string} />
        })
      }
    }

    return {
      tabs,
      hasExtensions,
      shouldPromoteExtensions
    }
  }, [board?.id])
}

export default BoardSettings
